<template>
  <div class="previewCanvas V1-design" :style="canvasStyle">
    <img
      v-for="tile in filteredShapes"
      :key="tile.id"
      class="tile"
      :src="tileImages[tile.id]"
      :style="
        tileStyle({ tile: tile, pattern: design.grid_properties.pattern })
      "
      draggable="false"
      ondragstart="return false;"
    />
  </div>
</template>

<script setup lang="ts">
// In Nuxt 3, these are auto-imported
// import { ref, computed, onMounted } from "vue";
import { tileStyle } from "~/utils/global";
import { tileImage, getImage } from "~/composables/global";

type CSSValue = string | number;

interface CSSProperties {
  [key: string]: CSSValue | undefined;
}

interface Color {
  image: string;
  label: string;
  sample?: string;
  image_3d?: string;
  new?: boolean;
  coaster?: string;
}

interface Shape {
  area: number;
  size: string;
  image: string;
  label: string;
  patterns: string[];
  tile_page: string;
  config_image: string;
  rotate?: number;
  modifier?: string;
  admin?: boolean;
}

interface Pattern {
  name: string;
  size: string;
  image: string;
  label: string;
  config_image: string;
}

interface Tile {
  x: number;
  y: number;
  id: string;
  color: Color;
  shape: Shape;
  pattern: Pattern;
  customRotation: number;
  original_x?: number;
  original_y?: number;
  pasteX?: number;
  pasteY?: number;
}

interface Design {
  shapes: Tile[];
  background_color?: string;
  canvas_width?: number;
  canvas_height?: number;
  isV1?: boolean;
  grid_properties: {
    pattern: string;
  };
}

interface Props {
  design: Design;
  width?: number;
  height?: number;
}

interface TileImageResponse {
  image: string;
}

const props = defineProps<Props>();
const defaultWidth = 820;
const defaultHeight = 400;

const tileImages = ref<Record<string, string>>({});

// Load all tile images
onMounted(async () => {
  const promises = props.design.shapes.map(async (tile: Tile) => {
    const imageResponse = await tileImage({ tile });
    if (imageResponse?.image) {
      tileImages.value[tile.id] = getImage({ image: imageResponse.image });
    }
  });
  await Promise.all(promises);
});

const filteredShapes = computed(() => {
  return props.design.shapes.filter((tile: Tile) => tileImages.value[tile.id]);
});

const canvasStyle = computed<CSSProperties>(() => ({
  width: `${defaultWidth}px`,
  height: `${defaultHeight}px`,
  position: "relative",
  backgroundColor: props.design.background_color || "#ffffff",
  overflow: "hidden",
}));
</script>

<style scoped>
.previewCanvas {
  height: 646px;
  position: relative;
  overflow: hidden;
}

.tile {
  position: absolute;
  pointer-events: none;
  user-select: none;
  transform-origin: center center;
}
</style>